import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, Loader2, Users } from 'lucide-react';

interface RouteInterestRow {
  id: string;
  full_name: string | null;
  email: string;
  phone: string | null;
  user_id: string | null;
  organization_id: string | null;
  created_at: string;
  organizations: { name: string } | null;
}

function toCsv(rows: RouteInterestRow[]): string {
  const header = ['Nombre', 'Correo', 'Celular', 'Empresa', 'Registrado', 'Fecha'];
  const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = rows.map(r =>
    [
      r.full_name ?? '',
      r.email,
      r.phone ?? '',
      r.organizations?.name ?? '',
      r.user_id ? 'Sí' : 'No',
      new Date(r.created_at).toLocaleString('es-CO'),
    ].map(esc).join(','),
  );
  return [header.map(esc).join(','), ...lines].join('\n');
}

export function RouteInterestsTab({ programId, programName }: { programId: string; programName: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['route-interests', programId],
    queryFn: async (): Promise<RouteInterestRow[]> => {
      const { data, error } = await supabase
        .from('route_interests')
        .select('id, full_name, email, phone, user_id, organization_id, created_at, organizations(name)')
        .eq('program_id', programId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as RouteInterestRow[];
    },
    enabled: !!programId,
  });

  const rows = data ?? [];

  const handleExport = () => {
    const blob = new Blob(['\uFEFF' + toCsv(rows)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `interesados-${programName.toLowerCase().replace(/\s+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Interesados
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Personas que pulsaron “Estoy Interesado” en esta ruta con acompañamiento.
          </p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={rows.length === 0}>
          <Download className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Aún no hay interesados registrados en esta ruta.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Correo</TableHead>
                <TableHead>Celular</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Cuenta</TableHead>
                <TableHead>Fecha</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.full_name || '—'}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>
                    {r.phone ? (
                      <a href={`https://wa.me/${r.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline whitespace-nowrap">
                        {r.phone}
                      </a>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{r.organizations?.name || 'Independiente'}</TableCell>
                  <TableCell>
                    {r.user_id ? (
                      <Badge variant="secondary">Registrado</Badge>
                    ) : (
                      <Badge variant="outline">Visitante</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString('es-CO')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default RouteInterestsTab;
